import React from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import { selectSetting } from "../../features/global/globalSlice";
import { useAppSelector } from "../../hooks/reduxHooks";
import { AUTH_LEVELS, TEAM_LOGO_URL } from "../../lib/globals";

const ClubhouseTable = (): React.JSX.Element => {
    const { teams: loadedTeams } = useAppSelector(state => state.clubhouse);
    const { user } = useAppSelector(state => state.auth);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "wibb_current_league_year"),
    )?.setting;
    const currentSeason = useAppSelector(state =>
        selectSetting(state, "wibb_current_league_season"),
    )?.setting;

    const teams = loadedTeams.map(team => {
        // Get's the ageLevel of the currently assigned league
        const leagueAgeLevel = team.ageLevels.find(a => {
            return (
                a.year === parseInt(currentYear) &&
                a.season.season === currentSeason
            );
        });

        // Get the most recent registered age level for a team. This will be displayed in the event a team does not have an assigned league for the current season, such as for a new registration or when the year/season changes
        const teamAgeLevel = team.ageLevels.reduce((prev, current) => {
            return new Date(current.year, current.season.id) >
                new Date(prev.year, prev.season.id)
                ? current
                : prev;
        }, team.ageLevels[0]);

        const division = team.leagues
            ?.find(l => l.year === currentYear)
            ?.name.match(/ ([A-Z+])$/);

        return {
            ...team,
            currentAgeLevel: leagueAgeLevel || teamAgeLevel,
            currentDivision: division ? division[1] : "\xFF",
        };
    });

    teams.sort((a, b) => {
        const nameCompare = a.name.localeCompare(b.name);

        if (nameCompare !== 0) {
            return nameCompare;
        }

        const ageLevelComparison =
            (a.currentAgeLevel ? parseInt(a.currentAgeLevel?.ageLevel) : 0) -
            (b.currentAgeLevel ? parseInt(b.currentAgeLevel?.ageLevel) : 0);

        if (ageLevelComparison !== 0) {
            return ageLevelComparison;
        }

        return (a.currentDivision || "").localeCompare(b.currentDivision || "");
    });

    return (
        <Table className="clubhouseTable" striped bordered responsive>
            <thead>
                <tr>
                    <th>Team Page</th>
                    <th>Logo</th>
                    <th>Team</th>
                    <th>Age Level</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    {user &&
                        AUTH_LEVELS.tourneymanagement.includes(
                            user.userLevel,
                        ) && <th></th>}
                </tr>
            </thead>
            <tbody>
                {teams.map(t => {
                    const currentTeamsLeague = t?.leagues?.find(
                        l => l.year === currentYear,
                    );
                    return (
                        <tr key={`${currentTeamsLeague?.id}-${t.id}`}>
                            <td>
                                <Link to={`/coachs-clubhouse/team/${t.id}`}>
                                    view
                                </Link>
                            </td>
                            <td>
                                {t.logo && (
                                    <img
                                        src={`${TEAM_LOGO_URL}${t.logo}`}
                                        alt={`${t.name} logo`}
                                        className="teamLogo img-fluid"
                                    />
                                )}
                            </td>
                            <td>
                                <h5>{t.name}</h5>
                                {currentTeamsLeague?.name ||
                                    t.currentAgeLevel?.ageLevel}
                            </td>
                            <td>{t.currentAgeLevel?.ageLevel}</td>
                            <td>{t.coach.firstName}</td>
                            <td>{t.coach.lastName}</td>
                            <td>{t.coach.phone}</td>
                            <td>
                                <a href={`mailto:${t.coach.email}`}>
                                    {t.coach.email}
                                </a>
                            </td>
                            {user &&
                                AUTH_LEVELS.tourneymanagement.includes(
                                    user.userLevel,
                                ) && <td>switch to user</td>}
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
};

export default ClubhouseTable;
